import { NavLink, useNavigate } from "react-router-dom";
import { Home, Heart, Video, User, LogOut } from "lucide-react";

import { signOut } from "@/lib/auth";
import { cn } from "@/lib/utils";

/** A nav item — either a route Link or a sign-out button (button is rendered
 *  inline so it lives alongside the others in the tab strip). */
export interface MobileNavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When set, end-match for active state. Default true. */
  end?: boolean;
}

interface Props {
  items: ReadonlyArray<MobileNavItem>;
  /** Render a Sign-out tab as the last item. */
  showSignOut?: boolean;
}

/**
 * Sticky bottom tab bar visible only on mobile (<768px). Mirrors the
 * Dashboard left rail so the same primary destinations are reachable
 * from a phone. Hidden on desktop, where the sidebar handles navigation.
 *
 * Use together with `<MobileBottomNavSpacer />` inside the page's main
 * column to add bottom padding equal to the bar height so the last row
 * of content isn't hidden behind it.
 */
export function MobileBottomNav({ items, showSignOut = true }: Props) {
  const navigate = useNavigate();

  const onSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[#e2e8f0] bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
      {items.map((item) => (
        <Tab key={item.to} {...item} />
      ))}
      {showSignOut ? (
        <button
          type="button"
          onClick={onSignOut}
          className="flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold text-[#94a3b8] hover:text-[#1a1a1a]"
        >
          <LogOut className="h-5 w-5" />
          Sign out
        </button>
      ) : null}
    </nav>
  );
}

/** Spacer that reserves room under page content for the bottom bar. */
export function MobileBottomNavSpacer() {
  return <div className="h-16 md:hidden" aria-hidden="true" />;
}

function Tab({ to, label, icon: Icon, end = true }: MobileNavItem) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold no-underline",
          isActive ? "text-[#2563eb]" : "text-[#94a3b8]",
        )
      }
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  );
}

/** Re-export icons so callers don't need a second lucide-react import. */
export const NavIcons = { Home, Heart, Video, User };
